import React from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {TFunction} from 'i18next';

import {Stack} from '@edna/components/primitives';
import {formatNumber} from '@edna/utils';

import {Text} from 'src/components/primitives';
import {
  ESmsPricingType,
  TSmsMauPackage,
  TSmsMauPricing,
  TSmsPricingStep,
  TTariffPricingDetail,
} from 'src/containers/TariffsCatalog';

import {formatPrice} from '../../utils';
import * as S from './style';

const SCALE_PRICING_TYPES: TTariffPricingDetail['label'][] = [
  ESmsPricingType.FEE,
  ESmsPricingType.MAU,
  ESmsPricingType.MULTI_MAU,
];

const getTrafficPrices = (step: TSmsPricingStep) =>
  step.trafficTypes.reduce<Record<string, string>>(
    (result, item) => ({...result, [item.label]: formatPrice(item.price, 4)}),
    {},
  );

const getStepLimit = (step: TSmsPricingStep, t: TFunction) =>
  step.upperLimit
    ? `${formatNumber(step.lowerLimit)} - ${formatNumber(step.upperLimit)}`
    : t('Tariffs:smsPricing.upperLimit', {limit: formatNumber(step.lowerLimit)});

const getPackageLimit = (item: TSmsMauPackage, t: TFunction) => {
  if (!item.upperLimit) {
    return t('Tariffs:smsPricing.packageLimits.from', {from: formatNumber(item.lowerLimit)});
  }

  if (item.upperLimit === item.lowerLimit) {
    return t('Tariffs:smsPricing.packageLimits.exact', {count: item.lowerLimit});
  }

  return t('Tariffs:smsPricing.packageLimits.range', {
    from: formatNumber(item.lowerLimit),
    to: formatNumber(item.upperLimit),
  });
};

const MauTable = React.memo<{pricing: TSmsMauPricing; currency: string}>(({pricing, currency}) => {
  const {t} = useTranslation();

  return (
    <>
      <Text secondary size="S">
        {t(`Tariffs:smsPricing.mauTitle.${pricing.label}`)}
      </Text>
      <S.Table columns={2}>
        <S.Tr noBorder>
          <S.Th>{t('Tariffs:smsPricing.packageLimit')}</S.Th>
          <S.Th>{t('Tariffs:smsPricing.packagePrice', {currency})}</S.Th>
        </S.Tr>
        {pricing.packages.map((item) => (
          <S.Tr key={item.order}>
            <S.Td>{getPackageLimit(item, t)}</S.Td>
            <S.Td>
              {item.perMessage
                ? t('Tariffs:smsPricing.perMessage', {price: formatPrice(item.amount)})
                : formatPrice(item.amount)}
            </S.Td>
          </S.Tr>
        ))}
      </S.Table>
    </>
  );
});

MauTable.displayName = 'MauTable';

type TProps = {
  pricingDetail: TTariffPricingDetail;
  currency: string;
};

export const SmsOperatorPricing = React.memo<TProps>(({pricingDetail, currency}) => {
  const {t} = useTranslation();

  const {data, label} = pricingDetail;
  const hasScale = SCALE_PRICING_TYPES.includes(label);

  const messagePrice = React.useMemo(
    () => (hasScale ? null : data.steps?.[0].trafficTypes?.[0].price),
    [hasScale, data],
  );

  const columns = React.useMemo(
    () =>
      hasScale && data.steps?.length ? data.steps[0].trafficTypes.map((type) => type.label) : [],
    [hasScale, data],
  );

  return (
    <S.PricingInfo>
      {!!data.recurrentAmount && (
        <Text secondary size="S">
          <Trans
            i18nKey="Tariffs:smsPricing.recurrentAmount"
            values={{currency, price: formatPrice(data.recurrentAmount)}}
          >
            <Text size="M" isInline />
          </Trans>
        </Text>
      )}
      {!!data.templateAmount && (
        <Text secondary size="S">
          <Trans
            i18nKey="Tariffs:smsPricing.templateAmount"
            values={{currency, price: formatPrice(data.templateAmount)}}
          >
            <Text size="M" isInline />
          </Trans>
        </Text>
      )}
      {!!messagePrice && (
        <Text secondary size="S">
          <Trans
            i18nKey="Tariffs:smsPricing.messagePrice"
            values={{currency, price: formatPrice(messagePrice)}}
          >
            <Text size="M" isInline />
          </Trans>
        </Text>
      )}
      {hasScale && !!columns.length && (
        <Stack size="2" isFullWidth>
          <Text secondary size="S">
            {t('Tariffs:smsPricing.feePricing')}
          </Text>
          <S.Table columns={columns.length + 1}>
            <S.Tr noBorder>
              <S.Th>{t('Tariffs:smsPricing.messagesLimit')}</S.Th>
              {columns.map((column) => (
                <S.Th key={column}>{t(`Tariffs:smsPricing.traffic.${column}`, {currency})}</S.Th>
              ))}
            </S.Tr>
            {data.steps?.map((step) => {
              const prices = getTrafficPrices(step);

              return (
                <S.Tr key={step.order}>
                  <S.Td>{getStepLimit(step, t)}</S.Td>
                  {columns.map((column) => (
                    <S.Td key={column}>{prices[column]}</S.Td>
                  ))}
                </S.Tr>
              );
            })}
          </S.Table>
        </Stack>
      )}
      {data.mau?.map((pricing) => (
        <MauTable key={pricing.label} pricing={pricing} currency={currency} />
      ))}
    </S.PricingInfo>
  );
});

SmsOperatorPricing.displayName = 'SmsOperatorPricing';
